import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Phone, Business } from '@mui/icons-material';
import { authAPI, RegisterData } from '../services/api';
import {
  validateEmail,
  validatePassword,
  validateConfirmPassword,
  validatePhone,
  validateRUC,
  validateRequired,
} from '../utils/validation';

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    telefono: '',
    ruc: '',
    razonSocial: '',
    rubro: '',
    numeroTrabajadores: 0,
    tipoUsuario: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Partial<RegisterData>>({});

  const tiposUsuario = [
    { value: 'empresa', label: 'Empresa' },
    { value: 'contador', label: 'Contador(a)' },
    { value: 'estudio_contable', label: 'Estudio Contable' },
    { value: 'estudiante', label: 'Estudiante' },
  ];

  const numeroTrabajadoresOpciones = [
    { value: '1', label: '1' },
    { value: '2-5', label: '2-5' },
    { value: '6-10', label: '6-10' },
    { value: '11-50', label: '11-50' },
    { value: '50+', label: '50 a más' },
  ];

  const rubros = [
    'Comercio',
    'Construcción',
    'Educación',
    'Manufactura',
    'Salud',
    'Servicios',
    'Tecnología',
    'Turismo',
    'Otro',
  ];

  const handleChange = (field: keyof RegisterData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'numeroTrabajadores' ? value : value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterData> = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData.telefono);
    if (phoneError) newErrors.telefono = phoneError;

    const rucError = validateRUC(formData.ruc);
    if (rucError) newErrors.ruc = rucError;

    const razonSocialError = validateRequired(formData.razonSocial, 'Razón Social');
    if (razonSocialError) newErrors.razonSocial = razonSocialError;

    const rubroError = validateRequired(formData.rubro, 'Rubro');
    if (rubroError) newErrors.rubro = rubroError;

    if (!formData.numeroTrabajadores) {
      newErrors.numeroTrabajadores = 'Número de trabajadores es requerido' as any;
    }

    const tipoUsuarioError = validateRequired(formData.tipoUsuario, 'Tipo de Usuario');
    if (tipoUsuarioError) newErrors.tipoUsuario = tipoUsuarioError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    const confirmPasswordError = validateConfirmPassword(formData.password, formData.confirmPassword);
    if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.register(formData);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Registro exitoso. Revisa tu correo para confirmar tu cuenta.',
          severity: 'success',
        });

        // Limpiar formulario
        setFormData({
          email: '',
          telefono: '',
          ruc: '',
          razonSocial: '',
          rubro: '',
          numeroTrabajadores: 0,
          tipoUsuario: '',
          password: '',
          confirmPassword: '',
        });
      }

    } catch (error: any) {
      let errorMessage = 'Error en el registro';
      
      if (error.message) {
        if (error.message.includes('email')) {
          errorMessage = 'El correo electrónico ya está registrado';
        } else if (error.message.includes('telefono')) {
          errorMessage = 'El número de teléfono ya está registrado';
        } else if (error.message.includes('ruc')) {
          errorMessage = 'El RUC ya está registrado';
        } else {
          errorMessage = error.message;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3, // Reducido de 4 a 3
          borderRadius: 3,
          maxWidth: 500, // Reducido de 600 a 500
          width: '100%',
          backgroundColor: '#f8f9fa',
          border: '2px solid #d0d7de',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}> {/* Reducido de mb: 4 a mb: 3 */}
          <Typography
            variant="h5" // Reducido de h4 a h5
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: 1,
            }}
          >
            Crear Cuenta
          </Typography>
          <Typography
            variant="h6" // Reducido de h5 a h6
            component="h2"
            sx={{
              fontWeight: 600,
              color: '#666',
              mb: 2,
            }}
          >
            Software CONTAX
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* CORREO ELECTRÓNICO - Línea completa */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* TELÉFONO y RUC - Media línea cada uno */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={formData.telefono}
                onChange={handleChange('telefono')}
                error={!!errors.telefono}
                helperText={errors.telefono}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RUC Perú"
                value={formData.ruc}
                onChange={handleChange('ruc')}
                error={!!errors.ruc}
                helperText={errors.ruc}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* RAZÓN SOCIAL - Línea completa */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Razón Social"
                value={formData.razonSocial}
                onChange={handleChange('razonSocial')}
                error={!!errors.razonSocial}
                helperText={errors.razonSocial}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '1rem', // Mismo tamaño que correo
                    height: '56px', // Misma altura que correo
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem', // Mismo tamaño de label
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1rem', // Mismo tamaño de texto
                  },
                }}
              />
            </Grid>

            {/* NÚMERO DE TRABAJADORES, RUBRO y TIPO DE USUARIO - Los 3 en una sola línea */}
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                error={!!errors.numeroTrabajadores}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '1rem', // Mismo tamaño que correo
                    height: '56px', // Misma altura que correo
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '1rem', // Mismo tamaño de label
                  },
                  '& .MuiSelect-select': {
                    fontSize: '1rem', // Mismo tamaño de texto
                  },
                }}
              >
                <InputLabel>N° Trabajadores</InputLabel>
                <Select
                  value={formData.numeroTrabajadores}
                  onChange={handleChange('numeroTrabajadores')}
                  label="N° Trabajadores"
                  required
                >
                  {numeroTrabajadoresOpciones.map((opcion) => (
                    <MenuItem key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                error={!!errors.rubro}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                    height: '56px',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                  '& .MuiSelect-select': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                }}
              >
                <InputLabel>Rubro</InputLabel>
                <Select
                  value={formData.rubro}
                  onChange={handleChange('rubro')}
                  label="Rubro"
                  required
                >
                  {rubros.map((rubro) => (
                    <MenuItem key={rubro} value={rubro}>
                      {rubro}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                error={!!errors.tipoUsuario}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                    height: '56px',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                  '& .MuiSelect-select': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                }}
              >
                <InputLabel>Tipo de Usuario</InputLabel>
                <Select
                  value={formData.tipoUsuario}
                  onChange={handleChange('tipoUsuario')}
                  label="Tipo de Usuario"
                  required
                >
                  {tiposUsuario.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* CONTRASEÑA y CONFIRMAR CONTRASEÑA - Misma línea */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                    height: '56px',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                    height: '56px',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(246, 177, 26, 0.15)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem', // Reducido de 1rem a 0.875rem
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2, // Reducido de 3 a 2
              mb: 2,
              py: 1.2, // Reducido de 1.5 a 1.2
              fontSize: '1rem', // Reducido de 1.1rem a 1rem
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              backgroundColor: '#000000',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#333333',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                backgroundColor: '#666666',
                color: '#cccccc',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#ffffff' }} />
            ) : (
              'Crear Cuenta'
            )}
          </Button>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              ¿Olvidaste tu contraseña?{' '}
              <Link
                to="/forgot-password"
                style={{
                  color: '#000000',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                Recupérala aquí
              </Link>
            </Typography>
          </Box>

          {/* NUEVO: Link al sistema CONTAX */}
          <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ¿Ya tienes cuenta?
            </Typography>
            <Button
              component="a"
              href="https://contax.sigex.pe/auth/jwt/login?returnTo=https%3A%2F%2Fcontax.sigex.pe%2Fdashboard"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                color: '#000000',
                borderColor: '#000000',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#000000',
                  color: '#ffffff',
                },
              }}
            >
              Acceder al Sistema CONTAX
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegisterForm;